import {Fragment, useState} from 'react'
import {Link as RouterLink} from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteIcon from '@mui/icons-material/Delete'
import Divider from '@mui/material/Divider'
import EditIcon from '@mui/icons-material/Edit'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import DeleteSetting from './DeleteSetting'
import Modal from '../../shared/Modal'
import SettingForm from './SettingForm'

import {connect} from '../../../state/hooks/tools'
import {createResource, updateResource} from '../../../lib/asyncMethods'
import {findRecordByAttribute, formatDate} from '../../../lib/data'
import {fullName, isEmpty} from '../../../lib/tools'
import {plainSettings} from '../../../lib/featureFlags'
import {SETTINGS} from '../../../state/actionTypes'

/**
 * The settings that are not flags — rows carrying a value rather than a switch.
 *
 * `ownableId` names whatever the row belongs to rather than a user in particular: METADATA_VERSION
 * belongs to an application, and the versions page is where it is chosen. So a row owned by
 * something with a page of its own sends the admin there instead of offering an editor that would
 * duplicate it; only a row belonging to nothing is edited, or deleted, here.
 */
const ownerFor = ({setting, applications, users}) => {
  if (!setting.ownableId)
    return {label: 'Global'}

  const application = findRecordByAttribute({records: applications, attribute: 'id', id: setting.ownableId})

  if (!isEmpty(application)) {
    return {
      label: `Application · ${application.name}`,
      path: `/admin/applications/${application.id}/versions`,
      action: 'Manage in Applications'
    }
  }

  const user = findRecordByAttribute({records: users, attribute: 'id', id: setting.ownableId})

  if (!isEmpty(user)) {
    return {
      label: `User · ${fullName(user)}`,
      path: `/admin/users/${user.id}`,
      action: 'Manage in Users'
    }
  }

  return {label: setting.ownableId}
}

const Detail = props => (
  <Stack direction='row' sx={{gap: '12px', justifyContent: 'space-between'}}>
    <Typography className='caption' variant='caption'>{props.label}</Typography>
    <Typography variant='body2' sx={{textAlign: 'right'}}>{props.children}</Typography>
  </Stack>
)

const OtherSettings = props => {
  const [showModal, setShowModal] = useState(false)
  const [data, setData] = useState({})
  const [showDelete, setShowDelete] = useState(false)
  const [settingToDelete, setSettingToDelete] = useState({})
  const settings = plainSettings(props.settings)

  const toggleModal = () => {
    setShowModal(!showModal)
  }

  const newSetting = () => {
    setData({})
    toggleModal()
  }

  const edit = setting => () => {
    setData(setting)
    toggleModal()
  }

  const toggleDelete = () => {
    setShowDelete(!showDelete)
  }

  const confirmDelete = setting => () => {
    setSettingToDelete(setting)
    toggleDelete()
  }

  const handleSubmit = attributes => {
    const request = isEmpty(data) ?
      props.createResource({endpoint: 'settings', attributes: {...attributes, featureFlag: false}}) :
      props.updateResource({endpoint: `settings/${data.id}`, attributes})

    request.then(response => {
      if (response.success)
        toggleModal()
    })
  }

  return (
    <Fragment>
      <Stack className='section'>
        <Stack direction='row' sx={{alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant='h6'>Other Settings</Typography>
          <Button onClick={newSetting} startIcon={<AddIcon />}>New Setting</Button>
        </Stack>
        <Divider />
        <Grid container rowSpacing={1} columnSpacing={{xs: 1, sm: 2, md: 3}} sx={{marginTop: '15px'}}>
          {
            settings.map(setting => {
              const owner = ownerFor({setting, applications: props.applications, users: props.users})

              return (
                <Grid key={setting.id} size={{xs: 12, md: 6}}>
                  <Card sx={{bgcolor: 'Background'}}>
                    <CardHeader component='h6' title={setting.name} />
                    <Divider />
                    <CardContent sx={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                      <Detail label='Owner'>
                        {owner.path ? <Link component={RouterLink} to={owner.path}>{owner.label}</Link> : owner.label}
                      </Detail>
                      <Detail label='Value'>{setting.value}</Detail>
                      <Detail label='Updated'>{formatDate(setting.updatedAt)}</Detail>
                    </CardContent>
                    <Divider />
                    <CardActions>
                      {
                        owner.path ?
                          <Link component={RouterLink} to={owner.path}>
                            <Button endIcon={<ChevronRightIcon />}>{owner.action}</Button>
                          </Link> :
                          <Fragment>
                            <IconButton onClick={edit(setting)} aria-label='edit' color='primary'>
                              <EditIcon />
                            </IconButton>
                            <IconButton disabled={props.loading} onClick={confirmDelete(setting)} color='error' aria-label='destroy'>
                              <DeleteIcon />
                            </IconButton>
                          </Fragment>
                      }
                    </CardActions>
                  </Card>
                </Grid>
              )
            })
          }
        </Grid>
        {
          isEmpty(settings) && !props.loading && (
            <Stack className='empty'>
              <AddIcon color='disabled' />
              <Typography variant='body2'>
                Nothing here yet. Rows an admin writes by hand land here, alongside the ones other pages
                keep — like the version an application is pinned to.
              </Typography>
            </Stack>
          )
        }
      </Stack>
      <Modal
        open={showModal}
        handleClose={toggleModal}
        disabled={props.loading}
        maxWidth='sm'
        buttonText='Done'
        subHeading='A named value the code reads. It reaches everyone unless another page gives it an owner.'
        title={isEmpty(data) ? 'New Setting' : 'Edit Setting'}>
          <SettingForm data={data} handleSubmit={handleSubmit} loading={props.loading} />
      </Modal>
      <DeleteSetting showModal={showDelete} toggleModal={toggleDelete} setting={settingToDelete} />
    </Fragment>
  )
}

const mapStateToProps = ({metadata, users}) => ({
  users: users.users || [],
  settings: metadata.settings,
  applications: metadata.applications,
  loading: metadata.loading
})

const actionCreators = ({metadata}) => ({
  createResource: createResource(metadata, SETTINGS),
  updateResource: updateResource(metadata, SETTINGS)
})

export default connect(mapStateToProps, actionCreators)(OtherSettings)
